/*
  Custom Select
  - Handles the custom select
  - Keeps custom options in sync with native select changes
*/
class IaCustomSelect extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector("[data-select-trigger]");
    this.valueSpan = this.querySelector("[data-select-value]");
    this.optionsContainer = this.querySelector(".ia-custom-options");
    this.customSelect = this.querySelector(".ia-custom-select");
    this.nativeSelect = this.querySelector("select");
    this.form = this.closest("form");

    // Initial render of options
    this.renderOptions();

    // Close select when clicking outside
    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) {
        this.customSelect.classList.remove("open");
      }
    });

    // Open/close trigger
    this.addEventListener("click", (e) => {
      this.customSelect.classList.toggle("open");
    });

    // Listen for option click events (delegated for re-rendered options)
    this.optionsContainer?.addEventListener("click", (e) => {
      const option = e.target.closest("[data-select-option]");
      if (option) {
        const value = option.dataset.value;
        this.setValue(value);
        this.nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    // Keep value in sync if user changes native select
    this.nativeSelect?.addEventListener("change", () => {
      this.setValue(this.nativeSelect.value);
    });

    // Reset value on form reset
    this.form?.addEventListener("reset", () => {
      setTimeout(() => {
        this.setValue(this.nativeSelect.options[0]?.value || "");
      }, 0);
    });

    // MutationObserver: watch for changes in the native select's options/text
    if (this.nativeSelect) {
      this.__observer = new MutationObserver(() => {
        this.renderOptions();
        this.setValue(this.nativeSelect.value);
      });
      this.__observer.observe(this.nativeSelect, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  // Render custom options to reflect the native select
  renderOptions() {
    if (!this.optionsContainer || !this.nativeSelect) return;
    // If there's an "All" or empty value, render as the first option
    let customOptionsHtml = '';
    // Find options in native select
    const nativeOptions = Array.from(this.nativeSelect.options);

    // By design, first option is label (disabled), second is "All" (value="")
    for (let opt of nativeOptions) {
      customOptionsHtml += `<span class="ia-custom-option${opt.selected ? ' selected' : ''}${opt.disabled ? ' disabled' : ''}" data-select-option data-value="${opt.value}">${opt.textContent}</span>`;
    }
    this.optionsContainer.innerHTML = customOptionsHtml;
  }

  setValue(value) {
    // Update the value display and underlying select
    // Find the native option text for this value
    let displayText = this.getAttribute("data-default-value") || "";
    if (this.nativeSelect) {
      const nativeOption = Array.from(this.nativeSelect.options).find(opt => opt.value === value);
      if (nativeOption) displayText = nativeOption.textContent;
      this.nativeSelect.value = value;
    }
    this.valueSpan.textContent = displayText;
    // Optional: visually mark selected option in the custom options
    if (this.optionsContainer) {
      Array.from(this.optionsContainer.children).forEach(optEl => {
        if (optEl.getAttribute("data-value") === value) {
          optEl.classList.add("selected");
        } else {
          optEl.classList.remove("selected");
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.__observer) this.__observer.disconnect();
  }
}

if (!customElements.get('ia-custom-select')) {
  customElements.define('ia-custom-select', IaCustomSelect);
}